import { ColDef, GridReadyEvent } from 'ag-grid-community'
import { useCallback } from 'react'

/**
 * Custom hook to reset ag-grid column state on grid ready.
 * This is necessary for ag-grid v25 which caches column state in browser storage
 * and doesn't always respect new columnDefs configurations.
 *
 * @param columnDefs - The column definitions to apply
 * @returns onGridReady callback that resets column state and applies column order
 */
const useResetGridColumnState = (
  columnDefs: ColDef[]
): ((params: GridReadyEvent) => void) => {
  return useCallback(
    (params: GridReadyEvent) => {
      // ag-grid v25 doesn't respect columnDefs changes when columns have cached state
      // Reset column state to ensure cellRendererParams and other configs are applied
      params.columnApi.resetColumnState()

      // Apply the desired column order
      const colIds = columnDefs
        .map((col) => col.colId || col.field)
        .filter((id): id is string => id !== undefined)

      const columnState = colIds.map((colId) => ({
        colId,
        sort: null,
        sortIndex: null,
      }))

      params.columnApi.applyColumnState({
        state: columnState,
        applyOrder: true,
      })
    },
    [columnDefs]
  )
}

export default useResetGridColumnState
